import { useState } from 'react';
import { clsx } from 'clsx';
import { Button } from '@/shared/ui/Button';
import { formatDate } from '@/shared/utils/format';
import type { CommunityOption, Pick } from '../api/communityApi';
import { metaFor } from './pickMeta';
import styles from './Community.module.css';

const BADGE_CLASS: Record<Pick['kind'], string> = {
  partner: styles.pickBadgePartner,
  nontraditional_partner: styles.pickBadgeNontrad,
  event: styles.pickBadgeEvent,
};

const KIND_LABEL: Record<Pick['kind'], string> = {
  partner: 'Referral partner',
  nontraditional_partner: 'Non-traditional partner',
  event: 'Community event',
};

/** The deadline line for an event with vendor booths: the date, and how soon it is. */
function deadlineLabel(iso: string): string {
  const days = Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
  const when = formatDate(iso, 'MMM d');
  if (days < 0) return `applications closed ${when}`;
  if (days === 0) return `applications close today (${when})`;
  if (days <= 14) return `applications close in ${days} day${days === 1 ? '' : 's'} (${when})`;
  return `applications close ${when}`;
}

export function PickCard({
  pick,
  reasons,
  onPlan,
  onSnooze,
  onDismiss,
  busy,
}: {
  pick: Pick;
  reasons: CommunityOption[];
  onPlan: (pick: Pick) => void;
  onSnooze: (pick: Pick) => void;
  onDismiss: (pick: Pick, reason: string) => void;
  busy: boolean;
}) {
  const [dismissing, setDismissing] = useState(false);
  const offered = reasons.filter((r) => pick.reasons.includes(r.key));
  return (
    <article className={styles.pick} aria-label={pick.name}>
      <div className={styles.pickMeta}>
        <span className={clsx(styles.pickBadge, BADGE_CLASS[pick.kind])}>{KIND_LABEL[pick.kind]}</span>
        <span>{metaFor(pick)}</span>
        {pick.hasCost && <span className={styles.spendTag}>Costs money: your call</span>}
      </div>
      <div>
        <div className={styles.pickName}>{pick.name}</div>
        {pick.org && <div className={styles.pickOrg}>{pick.org}</div>}
      </div>
      <div className={styles.pickWhy}>
        <strong>Why Joe picked this:</strong> {pick.why}
      </div>
      {pick.kind === 'event' && (pick.vendorInfo || pick.boothDeadline) && (
        <div className={styles.vendorBox}>
          <div className={styles.vendorTitle}>Taking part</div>
          <ul className={styles.vendorList}>
            {pick.vendorInfo?.cost && <li>Cost, as the event page puts it: "{pick.vendorInfo.cost}"</li>}
            {pick.boothDeadline && <li>Vendor or booth {deadlineLabel(pick.boothDeadline)}{pick.vendorInfo?.deadlineText ? ` ("${pick.vendorInfo.deadlineText}")` : ''}</li>}
            {pick.vendorInfo?.attendance && <li>Attendance, per the page: "{pick.vendorInfo.attendance}"</li>}
            {pick.vendorInfo?.pastVendors[0] && <li>Past vendors: "{pick.vendorInfo.pastVendors[0]}"</li>}
          </ul>
          {pick.vendorInfo?.registrationUrl && (
            <a className={styles.vendorLink} href={pick.vendorInfo.registrationUrl} target="_blank" rel="noreferrer">
              Vendor or sponsor application
            </a>
          )}
          <div className={styles.vendorNote}>Read from the event's own page; check the details there before you apply. Nothing is booked or paid by PracticeNova.</div>
        </div>
      )}
      <div className={styles.pickTouch}>
        <span>Suggested touch</span>
        <span className={styles.pickTouchChip}>{pick.suggestedTouchLabel}</span>
      </div>
      <div className={styles.sourceLine}>
        Source: {pick.source === 'npi' ? 'NPI registry' : pick.source === 'places' ? 'Google Maps' : pick.source === 'ical' ? 'calendar feed' : pick.source === 'jsonld' ? 'events page' : 'added by you'}
        {' · '}verified {formatDate(pick.lastVerified)}
        {pick.sourceUrl && (
          <>
            {' · '}
            <a href={pick.sourceUrl} target="_blank" rel="noreferrer">
              open listing
            </a>
          </>
        )}
      </div>
      <div className={styles.pickActions}>
        <Button variant="primary" size="sm" disabled={busy} onClick={() => onPlan(pick)}>
          {pick.kind === 'event' ? 'Add to my plans' : 'Plan a touch'}
        </Button>
        <Button variant="secondary" size="sm" disabled={busy} onClick={() => onSnooze(pick)}>
          {pick.state === 'snoozed' ? 'Snoozed once; snooze again to pass' : 'Snooze 30 days'}
        </Button>
        <button type="button" className={styles.linkButton} disabled={busy} aria-expanded={dismissing} onClick={() => setDismissing((v) => !v)}>
          Not for me
        </button>
      </div>
      {dismissing && (
        <div className={styles.reasons}>
          <div className={styles.reasonsTitle}>Help Joe learn. Why not?</div>
          <div className={styles.chips}>
            {offered.map((r) => (
              <button key={r.key} type="button" className={styles.chip} disabled={busy} onClick={() => onDismiss(pick, r.key)}>
                {r.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </article>
  );
}
